import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, TrendingUp, Users, Zap, Shield, Target, Clock, Sparkles, Star, Quote, Home as HomeIcon, ShieldCheck } from 'lucide-react';
import { LogoMarquee } from '../components/LogoMarquee';
import { USMap } from '../components/USMap';
import { useSEO } from '../hooks/useSEO';

const HERO_IMAGE = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_php-sample-build/artifacts/w0o0ahnd_ChatGPT%20Image%20Jul%2031%2C%202026%2C%2009_39_44%20PM.png';

// Simple Icons CDN — white monotone SVGs (guaranteed on-brand, no license issues).
const SI = (slug) => `https://cdn.simpleicons.org/${slug}/ffffff`;

const INTEGRATIONS = [
  { name: 'Salesforce',    icon: SI('salesforce') },
  { name: 'Zoho',          icon: SI('zoho') },
  { name: 'Dynamics 365',  icon: SI('microsoftdynamics365') },
  { name: 'HubSpot',       icon: SI('hubspot') },
  { name: 'Mailchimp',     icon: SI('mailchimp') },
  { name: 'Sender.net',    dot: '#7BAFDB', tagline: 'Email + SMS' },
  { name: 'Custom APIs',   dot: '#F59E0B', tagline: 'Webhooks / REST' },
];

const PARTNERS = [
  { name: 'Senior Life Insurance Agency', dot: '#4A8BC2', tagline: 'Final Expense' },
  { name: 'HealthMarkets',                dot: '#7BAFDB', tagline: 'Medicare / ACA' },
  { name: 'Medigap',                      dot: '#31638C', tagline: 'Supplemental Health' },
  { name: 'Connect Insurance',            dot: '#4A8BC2', tagline: 'Multi-Line' },
  { name: 'Powur',                        dot: '#F59E0B', tagline: 'Residential Solar' },
  { name: 'Palmetto Solar',               dot: '#7BBFFF', tagline: 'Solar Installer' },
  { name: 'Florida Roofing Guy',          dot: '#4A8BC2', tagline: 'Roofing' },
];

export default function Home() {
  const trustMetrics = [
    { label: '11+ Years in the Trenches', icon: Clock },
    { label: '2 Businesses Built From Zero', icon: TrendingUp },
    { label: 'Real-Time Delivery', icon: Zap },
    { label: 'Home + Insurance Verticals', icon: Target },
  ];

  const services = [
    { title: 'Real-Time Leads', description: 'Fresh, intent-driven leads the moment they are generated.', icon: Zap },
    { title: 'Data Solutions', description: 'Clean, targeted, compliant data sets built for your niche.', icon: Target },
    { title: 'Live Transfers', description: 'Warm prospects connected to your team in real time.', icon: TrendingUp },
    { title: 'Social Media Marketing', description: 'Paid campaigns engineered to generate demand.', icon: Sparkles },
    { title: 'Social Media Management', description: 'Full-service brand presence and audience growth.', icon: Users },
    { title: 'AI Automations', description: 'Smart workflows that qualify, route, and follow up.', icon: Shield },
  ];

  const homeVerticals = ['Residential Solar', 'Home Improvement', 'Roofing', 'Real Estate', 'Windows', 'HVAC', 'Remodeling'];
  const insuranceVerticals = ['Medicare', 'Final Expense', 'Life Insurance', 'ACA (Health)', 'Auto Insurance', 'Truck Insurance'];

  const differentiators = [
    { title: 'Real-Time, Not Recycled', icon: Zap, desc: 'Fresh leads at the moment of intent' },
    { title: 'Multi-Platform Generation', icon: Target, desc: 'Reach buyers across every channel' },
    { title: 'Conversion-First Mindset', icon: TrendingUp, desc: 'We measure by your close rate' },
    { title: 'Built-In Automation', icon: Shield, desc: 'Smart workflows out of the box' },
    { title: 'Vertical Expertise', icon: Users, desc: 'Deep knowledge of your market' },
  ];

  const testimonials = [
    { name: 'Marcus Chen', role: 'Insurance Agent', company: 'Chen Insurance Group', rating: 5, text: 'Noax cut our cost per acquisition by 47%. Their Medicare leads convert 3x better than any competitor we tried.', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
    { name: 'Sarah Williams', role: 'Solar Installer', company: 'Sunrise Solar Co.', rating: 5, text: 'Real-time leads changed our business. We connect with homeowners within seconds of their interest peaking.', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
    { name: 'David Rodriguez', role: 'Insurance Broker', company: 'Rodriguez & Associates', rating: 5, text: 'Best lead partner we have had in 15 years. Their live transfers close at nearly double the industry average.', avatar: 'https://randomuser.me/api/portraits/men/45.jpg' },
    { name: 'Jennifer Park', role: 'Home Improvement Specialist', company: 'Elite Home Renovations', rating: 5, text: 'The quality of leads is unmatched. Every prospect is genuinely interested and ready to talk numbers.', avatar: 'https://randomuser.me/api/portraits/women/68.jpg' },
    { name: 'Michael Torres', role: 'HVAC Contractor', company: 'ClimateFirst HVAC', rating: 5, text: 'Our booking rate jumped 62% in the first month. Noax truly understands the home services buyer.', avatar: 'https://randomuser.me/api/portraits/men/22.jpg' },
    { name: 'Amanda Foster', role: 'Life Insurance Agent', company: 'Foster Financial', rating: 5, text: 'Their AI automations qualify leads before I even pick up the phone. I close deals I would have missed.', avatar: 'https://randomuser.me/api/portraits/women/56.jpg' },
    { name: 'James Mitchell', role: 'Roofing Contractor', company: 'Mitchell Roofing Solutions', rating: 5, text: 'From dead leads to real appointments. Noax delivers homeowners with actual roof damage and budgets ready.', avatar: 'https://randomuser.me/api/portraits/men/60.jpg' },
    { name: 'Rachel Adams', role: 'Medicare Broker', company: 'Adams Senior Solutions', rating: 5, text: 'Compliance-first approach saved us. Every lead is TCPA-clean and delivered with full intent signals.', avatar: 'https://randomuser.me/api/portraits/women/12.jpg' },
    { name: 'Thomas Nguyen', role: 'Solar Sales Director', company: 'GreenPeak Energy', rating: 5, text: 'We scaled from 5 to 40 installations per month. Noax is the growth engine we needed for years.', avatar: 'https://randomuser.me/api/portraits/men/85.jpg' },
    { name: 'Lisa Bennett', role: 'Auto Insurance Agent', company: 'Bennett Auto Coverage', rating: 5, text: 'Live transfers pre-qualified by real people, not bots. That is the difference between closing and hoping.', avatar: 'https://randomuser.me/api/portraits/women/33.jpg' },
    { name: 'Kevin O\'Brien', role: 'Home Remodeling Owner', company: 'O\'Brien Custom Homes', rating: 5, text: 'Every lead comes with intent signals. We know exactly what the homeowner wants before the first call.', avatar: 'https://randomuser.me/api/portraits/men/70.jpg' },
    { name: 'Priya Sharma', role: 'ACA Insurance Specialist', company: 'HealthFirst Insurance', rating: 5, text: 'The most transparent lead partner we have worked with. Real numbers, real results, real accountability.', avatar: 'https://randomuser.me/api/portraits/women/79.jpg' },
  ];

  return (
    <div className="min-h-screen text-white overflow-hidden relative">
      {useSEO({
        title: 'Noax Media | Real-Time US Lead Generation for Medicare & Final Expense',
        description: 'Real-time US lead generation for Medicare Leads, Final Expense Leads, insurance, solar, and home services. High-intent leads delivered the moment buyers show intent.',
        path: '/',
      })}
      {/* Animated Background Layer - Global light */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#31638C] opacity-[0.05] rounded-full blur-3xl animate-drift"></div>
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.06] rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-[#31638C] opacity-[0.05] rounded-full blur-3xl animate-drift" style={{ animationDelay: '7s' }}></div>
      </div>

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 grain-overlay z-10"
        data-testid="hero-section"
      >
        <div className="absolute top-20 -left-20 w-96 h-96 bg-[#31638C] opacity-[0.12] rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute top-40 right-0 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.10] rounded-full blur-3xl animate-drift"></div>
        <div className="absolute inset-0 grid-pattern opacity-40"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div 
              className="lg:col-span-6"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#131E2C]/80 backdrop-blur-sm border border-[#31638C]/20 shadow-sm mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Sparkles size={14} className="text-[#31638C] mr-2" />
                <span className="text-xs font-medium text-[#31638C] tracking-wider uppercase">
                  Real-Time Lead Generation
                </span>
              </motion.div>

              <h1 
                className="text-5xl lg:text-7xl font-bold tracking-tighter leading-none mb-6 text-white"
                data-testid="hero-heading"
              >
                Real-Time Leads.<br />
                Real Conversions.<br />
                <span className="gradient-text">Real Growth.</span>
              </h1>
              <p className="text-lg text-gray-400 leading-relaxed mb-8 max-w-2xl">
                Noax Media delivers high-intent, real-time leads across home and insurance verticals — 
                powered by digital precision and 11 years of ground-level industry experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/about#contact"
                  className="inline-flex items-center justify-center px-8 py-3.5 bg-gradient-to-r from-[#31638C] to-[#31638C] text-white font-semibold rounded-full hover:-translate-y-1 transition-all duration-300 hover:shadow-xl hover:shadow-[#31638C]/40"
                  data-testid="hero-cta-primary"
                >
                  Get Leads That Close
                  <ArrowRight className="ml-2" size={18} />
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="lg:col-span-6 relative"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="relative animate-float">
                {/* Radial glow behind image */}
                <div className="absolute inset-0 bg-gradient-radial from-[#31638C]/25 via-transparent to-transparent blur-3xl"></div>

                {/* Static image with down shadow */}
                <img
                  src={HERO_IMAGE}
                  alt="Real-time lead generation analytics — exclusive Medicare leads and insurance leads dashboard"
                  className="relative w-full h-auto"
                  style={{
                    filter: 'drop-shadow(0 40px 40px rgba(0, 0, 0, 0.6))',
                  }}
                  data-testid="hero-image"
                />

                {/* Floating stat cards */}
                <motion.div
                  className="absolute bottom-4 -left-2 lg:-left-4 bg-[#131E2C] border border-[#5D676E]/25 rounded-xl p-4 shadow-xl backdrop-blur-sm z-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#31638C] to-[#31638C] flex items-center justify-center">
                      <TrendingUp className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">11+</div>
                      <div className="text-xs text-gray-500">Years Experience</div>
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  className="absolute top-8 -right-2 lg:-right-4 bg-[#131E2C] border border-[#5D676E]/25 rounded-xl p-4 shadow-xl backdrop-blur-sm z-10"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#31638C] to-[#4A8BC2] flex items-center justify-center animate-pulse">
                      <Zap className="text-white" size={20} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">Live</div>
                      <div className="text-xs text-gray-500">Real-Time</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#131E2C]/70 backdrop-blur-sm border-y border-[#5D676E]/25 py-8 overflow-hidden relative z-10" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {trustMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <motion.div
                  key={index}
                  className="flex items-center space-x-3"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`trust-metric-${index}`}
                >
                  <Icon className="text-[#31638C]" size={22} />
                  <span className="text-sm font-medium text-gray-300">{metric.label}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations Marquee */}
      <LogoMarquee
        heading="Integrates With Your Stack"
        subheading="Delivered straight into the tools you already run"
        items={INTEGRATIONS}
        direction="left"
        speedSec={45}
        data-testid="integrations-marquee"
      />

      {/* Who We Are */}
      <section className="py-20 lg:py-28 relative bg-[#0A1420]" data-testid="who-we-are">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#31638C] opacity-[0.05] rounded-full blur-3xl animate-drift"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-semibold">WHO WE ARE</span>
              <h2 
                className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-6 text-white"
              >
                Built by Operators,<br />
                <span className="gradient-text">Not Marketers</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6 text-lg">
                Noax Media was founded by individuals with over a decade of hands-on experience in lead generation 
                and performance marketing. We have built businesses and closed deals.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                We understand what differentiates a converting lead from a time-wasting one.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-[#31638C] font-semibold hover:text-[#31638C] transition-colors group"
                data-testid="learn-story-link"
              >
                Learn Our Story
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-radial from-[#31638C]/25 via-transparent to-transparent blur-3xl"></div>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                  alt="Noax Media lead generation team — 11 years of experience in real-time US lead generation for insurance and home services"
                  className="w-full"
                  style={{
                    filter: 'brightness(0.55) contrast(1.1) saturate(0.75)',
                    WebkitMaskImage: 'radial-gradient(ellipse 85% 85% at center, black 45%, transparent 100%)',
                    maskImage: 'radial-gradient(ellipse 85% 85% at center, black 45%, transparent 100%)',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1420]/40 via-transparent to-[#0A1420]/60 mix-blend-multiply pointer-events-none"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Snapshot */}
      <section className="py-20 lg:py-28 relative overflow-hidden z-10" data-testid="services-snapshot">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[#31638C]/30 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-semibold">WHAT WE DO</span>
            <h2 
              className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 text-white"
            >
              Everything You Need to<br />
              <span className="gradient-text">Fill Your Pipeline</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`service-card-${index}`}
                >
                  <Link
                    to="/services"
                    aria-label={`Learn more about ${service.title}`}
                    className="relative block h-full bg-[#131E2C] p-8 rounded-2xl border border-[#5D676E]/25 card-hover group shadow-sm hover:border-[#31638C]/50"
                  >
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#31638C]/10 to-[#4A8BC2]/10 border border-[#31638C]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="text-[#31638C]" size={24} />
                    </div>
                    <div className="text-3xl font-bold text-[#31638C]/30 mb-3">
                      0{index + 1}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-[#7BAFDB] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {service.description}
                    </p>
                    <span className="mt-5 inline-flex items-center text-[#31638C] text-sm font-semibold group-hover:text-[#4A8BC2] transition-colors">
                      Learn more
                      <ArrowRight className="ml-1.5 group-hover:translate-x-1 transition-transform" size={14} />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-[#31638C] font-semibold hover:text-[#31638C] transition-colors group"
              data-testid="explore-services-link"
            >
              Explore All Services
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Verticals We Serve */}
      <section className="py-20 lg:py-28 relative z-10" data-testid="verticals-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-semibold">VERTICALS</span>
            <h2 
              className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 mb-4 text-white"
            >
              Two Industries.<br />
              <span className="gradient-text">Total Coverage.</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              If your business lives in these markets, we already know your buyer.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Home Leads Flip Card */}
            <motion.div 
              className="flip-card cursor-pointer"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="home-leads-card"
            >
              <div className="flip-card-inner">
                {/* Front */}
                <div className="flip-card-front relative bg-gradient-to-br from-[#131E2C] to-[#1A2A3F] border border-[#5D676E]/25 flex flex-col items-center justify-center p-10 overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#31638C]/20 rounded-full blur-3xl"></div>
                  <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#4A8BC2]/15 rounded-full blur-3xl"></div>
                  <div className="relative flex flex-col items-center">
                    <div className="w-44 h-44 rounded-2xl bg-gradient-to-br from-[#31638C]/20 to-[#4A8BC2]/20 border border-[#31638C]/40 flex items-center justify-center mb-8 shadow-xl">
                      <HomeIcon className="text-[#4A8BC2]" size={100} strokeWidth={1.4} />
                    </div>
                    <h3 className="text-3xl font-bold text-white">Home Leads</h3>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back relative bg-gradient-to-br from-[#31638C] to-[#1F4266] border border-[#31638C]/50 p-10 overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
                  <div className="relative h-full flex flex-col">
                    <div className="flex items-center mb-6">
                      <HomeIcon className="text-white mr-3" size={32} strokeWidth={1.5} />
                      <h3 className="text-2xl font-bold text-white">Home Leads</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-3 flex-1">
                      {homeVerticals.map((vertical, index) => (
                        <div key={index} className="flex items-center" data-testid={`home-vertical-${index}`}>
                          <CheckCircle2 className="text-white mr-3 flex-shrink-0" size={18} />
                          <span className="text-white text-base font-medium">{vertical}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Insurance Leads Flip Card */}
            <motion.div 
              className="flip-card cursor-pointer"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="insurance-leads-card"
            >
              <div className="flip-card-inner">
                {/* Front */}
                <div className="flip-card-front relative bg-gradient-to-br from-[#131E2C] to-[#1A2A3F] border border-[#5D676E]/25 flex flex-col items-center justify-center p-10 overflow-hidden">
                  <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#4A8BC2]/20 rounded-full blur-3xl"></div>
                  <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#31638C]/15 rounded-full blur-3xl"></div>
                  <div className="relative flex flex-col items-center">
                    <div className="w-44 h-44 rounded-2xl bg-gradient-to-br from-[#31638C]/20 to-[#4A8BC2]/20 border border-[#31638C]/40 flex items-center justify-center mb-8 shadow-xl">
                      <ShieldCheck className="text-[#4A8BC2]" size={100} strokeWidth={1.4} />
                    </div>
                    <h3 className="text-3xl font-bold text-white">Insurance Leads</h3>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back relative bg-gradient-to-br from-[#31638C] to-[#1F4266] border border-[#31638C]/50 p-10 overflow-hidden">
                  <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-3xl"></div>
                  <div className="relative h-full flex flex-col">
                    <div className="flex items-center mb-6">
                      <ShieldCheck className="text-white mr-3" size={32} strokeWidth={1.5} />
                      <h3 className="text-2xl font-bold text-white">Insurance Leads</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-3 flex-1">
                      {insuranceVerticals.map((vertical, index) => (
                        <div key={index} className="flex items-center" data-testid={`insurance-vertical-${index}`}>
                          <CheckCircle2 className="text-white mr-3 flex-shrink-0" size={18} />
                          <span className="text-white text-base font-medium">{vertical}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 relative overflow-hidden z-10" data-testid="testimonials-section">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#31638C]/30 to-transparent"></div>
        
        <motion.div 
          className="text-center mb-16 relative z-10 px-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-semibold">TESTIMONIALS</span>
          <h2 
            className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 text-white"
          >
            Trusted by <span className="gradient-text">Top Operators</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mt-4">
            Real results from agents, brokers, and installers in the field.
          </p>
        </motion.div>

        <div className="relative">
          <div className="flex gap-6 animate-marquee whitespace-nowrap" style={{ animationDuration: '60s' }}>
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <TestimonialCard key={`testimonial-${index}`} testimonial={testimonial} />
            ))}
          </div>
        </div>

        <div className="absolute top-0 left-0 h-full w-32 bg-gradient-to-r from-[#0A1420] to-transparent pointer-events-none z-10"></div>
        <div className="absolute top-0 right-0 h-full w-32 bg-gradient-to-l from-[#0A1420] to-transparent pointer-events-none z-10"></div>
      </section>

      {/* Nationwide Coverage Map */}
      <USMap />

      {/* Why Noax Media */}
      <section className="py-20 lg:py-28 relative overflow-hidden z-10" data-testid="why-noax">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#31638C] opacity-10 rounded-full blur-3xl animate-drift"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#4A8BC2] opacity-10 rounded-full blur-3xl animate-float-slow"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#31638C] font-semibold">WHY NOAX MEDIA</span>
            <h2 
              className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 text-white"
            >
              Why Companies Switch to Us<br />
              <span className="gradient-text">— and Stay</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {differentiators.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="relative bg-[#131E2C] p-6 rounded-2xl border border-[#5D676E]/25 card-hover group text-center shadow-sm"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`differentiator-${index}`}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#31638C] to-[#31638C] mb-4 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-[#31638C]/30">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners Marquee (above footer) */}
      <LogoMarquee
        heading="Trusted Partners"
        subheading="Teams we have grown revenue with"
        items={PARTNERS}
        direction="right"
        speedSec={50}
        data-testid="partners-marquee"
      />

      {/* Final CTA */}
      <section className="relative py-24 lg:py-32 overflow-hidden z-10" data-testid="final-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#31638C] via-[#31638C] to-[#1F4266] animate-gradient"></div>
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#131E2C] opacity-10 rounded-full blur-3xl animate-drift"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#131E2C] opacity-10 rounded-full blur-3xl animate-float-slow"></div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-4xl lg:text-6xl font-bold tracking-tight mb-6 text-white leading-tight"
            >
              Stop Chasing Cold Lists.<br />
              Start Closing Real Prospects.
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl mx-auto">
              Your competitors are already talking to your next customer. Let us make sure that customer is talking to you first.
            </p>
            <Link
              to="/about#contact"
              className="group inline-flex items-center px-10 py-4 bg-[#131E2C] text-[#31638C] font-semibold rounded-full hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              data-testid="final-cta-button"
            >
              Get Started Today
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function TestimonialCard({ testimonial }) {
  return (
    <div className="flex-shrink-0 w-96 bg-[#131E2C] border border-[#5D676E]/25 rounded-2xl p-6 hover:border-[#31638C]/40 hover:shadow-lg transition-all duration-300 shadow-sm" data-testid={`testimonial-${testimonial.name.replace(' ', '-').toLowerCase()}`}>
      <Quote className="text-[#31638C]/40 mb-3" size={28} />
      <div className="flex mb-3">
        {Array.from({ length: testimonial.rating }).map((_, i) => (
          <Star key={i} className="text-[#F59E0B] fill-[#F59E0B]" size={16} />
        ))}
      </div>
      <p className="text-gray-300 text-sm leading-relaxed mb-4 whitespace-normal">
        {testimonial.text}
      </p>
      <div className="flex items-center pt-4 border-t border-[#5D676E]/25">
        <img
          src={testimonial.avatar}
          alt={`${testimonial.name} — Noax Media client testimonial for real-time lead generation`}
          className="w-10 h-10 rounded-full mr-3 border border-[#31638C]/30"
        />
        <div>
          <div className="text-sm font-semibold text-white">{testimonial.name}</div>
          <div className="text-xs text-gray-500">{testimonial.role} · {testimonial.company}</div>
        </div>
      </div>
    </div>
  );
}
